import { toast } from "react-toastify";
import { all, put, takeLatest } from "redux-saga/effects";

// Apis
import {
  getZonesFn,
  createZoneFn,
  updateZoneFn,
  deleteZoneFn,
  getSubZonesFn,
  createSubZonesFn,
  updateSubZonesFn,
  deleteSubZonesFn,
} from "../apis/ZonesAndSubZonesAPI";

// Constants and Utils
import { ZonesAndSubZones } from "../constants/action-constants/ZonesAndSubZonesActionConstants";
import { getErrorMessage } from "../../utils/util";

// util function to get and update data
function* getZonesAndUpdateData(regionId, pageNo, pageSize) {
  const data = yield getZonesFn(regionId, pageNo, pageSize);
  const { items, pagination } = data || {};

  yield put({
    type: ZonesAndSubZones.GET_ZONES_SUCCESS,
    payload: { zones: items, pagination },
  });
}

export function* getZones(action) {
  const { regionId } = action.payload;
  try {
    yield getZonesAndUpdateData(regionId);
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.GET_ZONES_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export function* createZone(action) {
  const { zoneBean, cityId } = action.payload;
  try {
    // create
    yield createZoneFn(zoneBean);

    // get updated zones
    if (cityId) {
      yield getZonesAndUpdateData(cityId);
    }

    // final create success
    yield put({
      type: ZonesAndSubZones.CREATE_ZONE_SUCCESS,
    });
    toast.success("Successfully created zone");
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.CREATE_ZONE_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export function* updateZone(action) {
  const { regionId, zoneBean, cityId } = action.payload;
  try {
    // update
    yield updateZoneFn(regionId, zoneBean);

    // get updated zones
    if (cityId) {
      yield getZonesAndUpdateData(cityId);
    }

    // final update success
    yield put({
      type: ZonesAndSubZones.UPDATE_ZONE_SUCCESS,
      payload: { regionId },
    });
    toast.success("Successfully updated zone");
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.UPDATE_ZONE_FAILURE,
      payload: { regionId, error },
    });
    toast.error(errorMessage);
  }
}

export function* deleteZone(action) {
  const { regionId, cityId } = action.payload;
  try {
    // delete
    yield deleteZoneFn(regionId, cityId);

    // get updated zones
    yield getZonesAndUpdateData(cityId);

    // final delete success
    yield put({
      type: ZonesAndSubZones.DELETE_ZONE_SUCCESS,
      payload: { regionId },
    });
    toast.success("Successfully deleted zone");
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.DELETE_ZONE_FAILURE,
      payload: { regionId, error },
    });
    toast.error(errorMessage);
  }
}

// update and utils function to get subZones
function* getSubZonesAndUpdateData(
  cityId,
  zoneId,
  searchText,
  pageNo,
  pageSize
) {
  const data = yield getSubZonesFn(
    cityId,
    zoneId,
    searchText,
    pageNo,
    pageSize
  );
  const { items, pagination } = data || {};

  // success
  yield put({
    type: ZonesAndSubZones.GET_SUB_ZONES_SUCCESS,
    payload: { subZones: items, pagination },
  });
}

export function* getSubZones(action) {
  const { cityId, zoneId, searchText, pageNo, pageSize } = action.payload;
  try {
    // get sub zones
    yield getSubZonesAndUpdateData(
      cityId,
      zoneId,
      searchText,
      pageNo,
      pageSize
    );
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.GET_SUB_ZONES_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export function* createSubZones(action) {
  const { cityId, subZonesBean } = action.payload;
  try {
    // create
    yield createSubZonesFn(cityId, subZonesBean);

    // get latest sub zones
    const { zoneId } = subZonesBean || {};
    yield getSubZonesAndUpdateData(cityId, zoneId);

    // final create success
    yield put({
      type: ZonesAndSubZones.CREATE_SUB_ZONES_SUCCESS,
    });
    toast.success("Successfully created sub zones");
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.CREATE_SUB_ZONES_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export function* updateSubZones(action) {
  const { cityId, subzoneListId, subZonesBean } = action.payload;
  try {
    // update
    yield updateSubZonesFn(cityId, subzoneListId, subZonesBean);

    // get latest sub zones
    const { zoneId } = subZonesBean || {};
    yield getSubZonesAndUpdateData(cityId, zoneId);

    // final update success
    yield put({
      type: ZonesAndSubZones.UPDATE_SUB_ZONES_SUCCESS,
    });
    toast.success("Successfully updated sub zones");
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.UPDATE_SUB_ZONES_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export function* deleteSubZones(action) {
  const { cityId, zoneId, subzoneListId } = action.payload;
  try {
    // delete
    yield deleteSubZonesFn(subzoneListId);

    // get latest sub zones
    yield getSubZonesAndUpdateData(cityId, zoneId);

    // final delete success
    yield put({
      type: ZonesAndSubZones.DELETE_SUB_ZONES_SUCCESS,
    });
    toast.success("Successfully updated sub zones");
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: ZonesAndSubZones.DELETE_SUB_ZONES_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export default function* root() {
  yield all([
    takeLatest(ZonesAndSubZones.GET_ZONES, getZones),
    takeLatest(ZonesAndSubZones.CREATE_ZONE, createZone),
    takeLatest(ZonesAndSubZones.UPDATE_ZONE, updateZone),
    takeLatest(ZonesAndSubZones.DELETE_ZONE, deleteZone),
    takeLatest(ZonesAndSubZones.GET_SUB_ZONES, getSubZones),
    takeLatest(ZonesAndSubZones.CREATE_SUB_ZONES, createSubZones),
    takeLatest(ZonesAndSubZones.UPDATE_SUB_ZONES, updateSubZones),
    takeLatest(ZonesAndSubZones.DELETE_SUB_ZONES, deleteSubZones),
  ]);
}
